// src/pages/CustomerSignout.jsx
import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import '../styles/customer.css';

function CustomerSignout() {
  const { salonId } = useParams();
  const [salonConfig, setSalonConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSalonConfig = async () => {
      try {
        if (!salonId) {
          throw new Error('Salon ID is not available');
        }
        
        const config = await getSalonConfig(salonId);
        if (config) {
          setSalonConfig(config);
        }
      } catch (err) {
        console.error('Error fetching salon config:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSalonConfig();
  }, [salonId]);

  // Helper function to get salon config - UPDATED to use 'salons' collection
  const getSalonConfig = async (salonId) => {
    try {
      const docRef = doc(db, 'salons', salonId); // CHANGED from 'salonConfigs'
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching salon config:', error);
      throw error;
    }
  };

  // Helper function to safely render fields
  const safeRender = (field) => {
    if (field === null || field === undefined) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      if (field.name) return field.name;
      if (field.description) return field.description;
      try {
        return JSON.stringify(field);
      } catch (e) {
        return '';
      }
    }
    return String(field);
  };

  // Loading state
  if (loading) {
    return (
      <div className="customer-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-container">
      <main className="customer-main">
        <div className="signout-content">
          <div className="signout-card">
            {/* Salon Logo */}
            {salonConfig?.branding?.logo?.url && (
              <div className="signout-logo">
                <img 
                  src={salonConfig.branding.logo.url} 
                  alt="Salon Logo" 
                  className="signout-salon-logo"
                />
              </div>
            )}
            
            {/* Signout Message */}
            <div className="signout-message">
              <div className="signout-icon">✓</div>
              <h1>You have been signed out</h1>
              <p>Thank you for visiting {safeRender(salonConfig?.branding?.name) || 'our salon'}!</p>
              <p>We hope to see you again soon.</p>
            </div>
            
            {/* Action Buttons */}
            <div className="signout-actions">
              <Link 
                to={`/BespokeBeauty/${salonId}/customer/login`}
                className="signin-button"
                style={{
                  backgroundColor: salonConfig?.branding?.colors?.primary || '#b6a593',
                  color: 'white'
                }}
              >
                Sign Back In
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      {/* Follow Us Section - Same as CustomerDashboard */}
      {(salonConfig?.contact?.socialMedia?.facebook || 
        salonConfig?.contact?.socialMedia?.instagram || 
        salonConfig?.contact?.socialMedia?.twitter) && (
        <div className="follow-us-section">
          <h2 style={{ color: '#444 !important', fontWeight: 'bold', fontSize: '24px' }}>Follow Us</h2>
          <div className="social-media-icons">
            {salonConfig?.contact?.socialMedia?.facebook && (
              <a 
                href={salonConfig.contact.socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
                aria-label="Follow us on Facebook"
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
                </svg>
              </a>
            )}
            
            {salonConfig?.contact?.socialMedia?.twitter && (
              <a 
                href={salonConfig.contact.socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
                aria-label="Follow us on Twitter/X"
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#000000"/>
                </svg>
              </a>
            )}
            
            {salonConfig?.contact?.socialMedia?.instagram && (
              <a 
                href={salonConfig.contact.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon-link"
                aria-label="Follow us on Instagram"
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="url(#instagram-gradient)"/>
                  <defs>
                    <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#405DE6"/>
                      <stop offset="25%" stopColor="#5B51D8"/>
                      <stop offset="50%" stopColor="#833AB4"/>
                      <stop offset="75%" stopColor="#C13584"/>
                      <stop offset="100%" stopColor="#E1306C"/>
                    </linearGradient>
                  </defs>
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerSignout;